/**
 * Phone parsing y formato internacional.
 *
 * - `isoToFlag(iso)` convierte un código ISO 3166-1 alpha-2 (ej. 'MX',
 *   'US', 'BR') al emoji bandera correspondiente usando Regional Indicator
 *   Symbols (U+1F1E6 ... U+1F1FF). Esto evita mantener un mapping ISO →
 *   emoji: cualquier código ISO válido produce su bandera automáticamente.
 *
 * - `parsePhone(raw)` detecta el prefijo de país (longest match contra
 *   PREFIX_TO_ISO), separa el número nacional y aplica el formato local
 *   por país. Si no se detecta país, asume MX para 10 dígitos sueltos
 *   (default razonable en el contexto Kalan EdoMex). Si el formato es
 *   irreconocible, devuelve el input tal cual sin romper la UI.
 *
 * Para agregar soporte de un país nuevo:
 *   1. Añadir su prefijo a PREFIX_TO_ISO.
 *   2. (opcional) Añadir un patrón de formato nacional en formatNational().
 *      Sin esto, cae al formato genérico de agrupación 2/3/4 dígitos.
 */

// ISO 3166-1 alpha-2 por prefijo telefónico internacional. Lista no
// exhaustiva — cubre los países con más probabilidad de aparecer en el
// flujo de reportantes (LATAM + España + países de origen frecuente de
// llamadas internacionales). Para agregar más, basta con añadir aquí.
const PREFIX_TO_ISO = {
  '+1':   'US',  // EEUU / Canadá / Caribe NANP — bandera US como representante
  '+7':   'RU',  // Rusia
  '+20':  'EG',  // Egipto
  '+27':  'ZA',  // Sudáfrica
  '+30':  'GR',  // Grecia
  '+31':  'NL',  // Países Bajos
  '+32':  'BE',  // Bélgica
  '+33':  'FR',  // Francia
  '+34':  'ES',  // España
  '+39':  'IT',  // Italia
  '+40':  'RO',  // Rumania
  '+41':  'CH',  // Suiza
  '+43':  'AT',  // Austria
  '+44':  'GB',  // Reino Unido
  '+45':  'DK',  // Dinamarca
  '+46':  'SE',  // Suecia
  '+47':  'NO',  // Noruega
  '+48':  'PL',  // Polonia
  '+49':  'DE',  // Alemania
  '+51':  'PE',  // Perú
  '+52':  'MX',  // México
  '+53':  'CU',  // Cuba
  '+54':  'AR',  // Argentina
  '+55':  'BR',  // Brasil
  '+56':  'CL',  // Chile
  '+57':  'CO',  // Colombia
  '+58':  'VE',  // Venezuela
  '+60':  'MY',  // Malasia
  '+61':  'AU',  // Australia
  '+62':  'ID',  // Indonesia
  '+63':  'PH',  // Filipinas
  '+64':  'NZ',  // Nueva Zelanda
  '+65':  'SG',  // Singapur
  '+81':  'JP',  // Japón
  '+82':  'KR',  // Corea del Sur
  '+84':  'VN',  // Vietnam
  '+86':  'CN',  // China
  '+90':  'TR',  // Turquía
  '+91':  'IN',  // India
  '+92':  'PK',  // Pakistán
  '+93':  'AF',  // Afganistán
  '+94':  'LK',  // Sri Lanka
  '+95':  'MM',  // Myanmar
  '+98':  'IR',  // Irán
  '+212': 'MA',  // Marruecos
  '+213': 'DZ',  // Argelia
  '+216': 'TN',  // Túnez
  '+218': 'LY',  // Libia
  '+220': 'GM',  // Gambia
  '+225': 'CI',  // Costa de Marfil
  '+233': 'GH',  // Ghana
  '+234': 'NG',  // Nigeria
  '+254': 'KE',  // Kenia
  '+351': 'PT',  // Portugal
  '+353': 'IE',  // Irlanda
  '+354': 'IS',  // Islandia
  '+358': 'FI',  // Finlandia
  '+359': 'BG',  // Bulgaria
  '+380': 'UA',  // Ucrania
  '+420': 'CZ',  // República Checa
  '+421': 'SK',  // Eslovaquia
  '+501': 'BZ',  // Belice
  '+502': 'GT',  // Guatemala
  '+503': 'SV',  // El Salvador
  '+504': 'HN',  // Honduras
  '+505': 'NI',  // Nicaragua
  '+506': 'CR',  // Costa Rica
  '+507': 'PA',  // Panamá
  '+509': 'HT',  // Haití
  '+591': 'BO',  // Bolivia
  '+593': 'EC',  // Ecuador
  '+595': 'PY',  // Paraguay
  '+597': 'SR',  // Surinam
  '+598': 'UY',  // Uruguay
  '+962': 'JO',  // Jordania
  '+963': 'SY',  // Siria
  '+966': 'SA',  // Arabia Saudita
  '+971': 'AE',  // Emiratos Árabes
  '+972': 'IL',  // Israel
  '+974': 'QA',  // Catar
  '+976': 'MN',  // Mongolia
  '+977': 'NP',  // Nepal
  '+992': 'TJ',  // Tayikistán
  '+994': 'AZ',  // Azerbaiyán
  '+995': 'GE',  // Georgia
}

// Prefijos ordenados de mayor a menor longitud para hacer longest-match.
// Sin esto, '+1' matchearía antes que '+1242' (Bahamas) y nos daríamos un
// false positive de US para números caribeños del bloque NANP.
const PREFIXES_SORTED = Object.keys(PREFIX_TO_ISO).sort(function(a, b) {
  return b.length - a.length
})

/**
 * Convierte un ISO 3166-1 alpha-2 a emoji bandera.
 *   'MX' → '🇲🇽', 'US' → '🇺🇸', 'BR' → '🇧🇷'
 * Funciona para cualquier ISO válido sin mantener un mapping de banderas.
 */
export function isoToFlag(iso) {
  if (!iso || iso.length !== 2) return ''
  const base = 0x1F1E6
  const A = 'A'.charCodeAt(0)
  const upper = iso.toUpperCase()
  return String.fromCodePoint(
    base + upper.charCodeAt(0) - A,
    base + upper.charCodeAt(1) - A
  )
}

/**
 * Aplica formato nacional según el país detectado. Sin patrón explícito,
 * usa un fallback genérico de agrupación.
 */
function formatNational(national, iso) {
  if (!national) return ''
  // México: (55) 1234-5678 (10 dígitos)
  if (iso === 'MX' && /^\d{10}$/.test(national)) {
    return '(' + national.slice(0, 2) + ') ' + national.slice(2, 6) + '-' + national.slice(6)
  }
  // EEUU/Canadá: (555) 123-4567 (10 dígitos NANP)
  if ((iso === 'US' || iso === 'CA') && /^\d{10}$/.test(national)) {
    return '(' + national.slice(0, 3) + ') ' + national.slice(3, 6) + '-' + national.slice(6)
  }
  // España: 555 12 34 56 (9 dígitos)
  if (iso === 'ES' && /^\d{9}$/.test(national)) {
    return national.slice(0, 3) + ' ' + national.slice(3, 5) + ' ' + national.slice(5, 7) + ' ' + national.slice(7)
  }
  // Brasil: (11) 9 1234-5678 (10-11 dígitos con o sin 9 inicial de móvil)
  if (iso === 'BR' && /^\d{10,11}$/.test(national)) {
    if (national.length === 11) {
      return '(' + national.slice(0, 2) + ') ' + national.slice(2, 3) + ' ' + national.slice(3, 7) + '-' + national.slice(7)
    }
    return '(' + national.slice(0, 2) + ') ' + national.slice(2, 6) + '-' + national.slice(6)
  }
  // Argentina: (11) 1234-5678 (10 dígitos)
  if (iso === 'AR' && /^\d{10}$/.test(national)) {
    return '(' + national.slice(0, 2) + ') ' + national.slice(2, 6) + '-' + national.slice(6)
  }
  // Fallback: agrupar últimos 4 dígitos, los 4 anteriores, y el resto al
  // inicio. Funciona razonablemente para 7-12 dígitos sin patrón conocido.
  if (national.length >= 8) {
    const last4 = national.slice(-4)
    const mid4 = national.slice(-8, -4)
    const head = national.slice(0, -8)
    return (head ? head + ' ' : '') + mid4 + '-' + last4
  }
  if (national.length >= 7) {
    return national.slice(0, -4) + '-' + national.slice(-4)
  }
  return national
}

/**
 * Parsea un número telefónico raw y retorna info estructurada.
 *
 * @param {string} raw — número en cualquier formato (con o sin +, espacios,
 *   guiones, paréntesis).
 * @returns {{ iso: string, prefix: string, national: string, formatted: string, flag: string } | null}
 *   `null` si el input está vacío.
 */
export function parsePhone(raw) {
  if (!raw) return null
  let clean = String(raw).replace(/[^\d+]/g, '')
  if (!clean) return null

  const hadPlus = clean.charAt(0) === '+'
  let prefix = ''
  let iso = ''

  // Caso 1: el número viene con + → buscar prefijo (longest match).
  if (hadPlus) {
    for (let i = 0; i < PREFIXES_SORTED.length; i++) {
      if (clean.indexOf(PREFIXES_SORTED[i]) === 0) {
        prefix = PREFIXES_SORTED[i]
        iso = PREFIX_TO_ISO[prefix]
        break
      }
    }
  } else if (clean.length > 10) {
    // Caso 2: sin + y con MÁS de 10 dígitos → puede ser código país +
    // nacional. Probamos los dígitos iniciales contra los prefijos.
    // CRÍTICO: un número de EXACTAMENTE 10 dígitos es nacional mexicano (sin
    // clave de país) y NO debe matchear prefijos contra sus dígitos iniciales
    // (antes "7006…"→Rusia, "55…"→Brasil, "1…"→USA). Esos caen al default
    // MX de abajo, respetando los 10 dígitos del número telefónico.
    for (let i = 0; i < PREFIXES_SORTED.length; i++) {
      const p = PREFIXES_SORTED[i].substring(1) // sin el +
      if (clean.indexOf(p) === 0 && clean.length > p.length) {
        prefix = PREFIXES_SORTED[i]
        iso = PREFIX_TO_ISO[prefix]
        clean = '+' + clean
        break
      }
    }
  }

  // Caso 3: sin clave de país detectada.
  //  - Si NO traía + → es un número nacional sin clave de país → MX por
  //    default (contexto del proyecto: reportantes mayormente mexicanos).
  //    Bandera 🇲🇽. Si son 10 dígitos, marcamos prefix +52.
  //  - Si traía + pero el prefijo es desconocido → internacional sin
  //    identificar; NO inventamos MX, devolvemos sin bandera.
  if (!iso) {
    if (!hadPlus) {
      iso = 'MX'
      if (/^\d{10}$/.test(clean)) prefix = '+52'
    } else {
      return {
        iso: '',
        prefix: '',
        national: clean,
        formatted: String(raw),
        flag: '',
      }
    }
  }

  const national = prefix && clean.indexOf(prefix) === 0
    ? clean.substring(prefix.length)
    : clean

  return {
    iso,
    prefix,
    national,
    formatted: formatNational(national, iso),
    flag: isoToFlag(iso),
  }
}
